
export const strategies = [
  {
    id: 'sudoku-beginner-guide',
    // Chinese (Default)
    title_zh: '数独终极入门指南：15分钟学会所有基本规则与技巧',
    summary_zh: '从零开始掌握数独！本文详解数独起源、基本规则、十字排除法与唯一余数法，助你轻松开启数独之旅。',
    readTime_zh: '15 分钟阅读',
    content_zh: `
      <h2>什么是数独？起源与规则详解</h2>
      <p>数独（Sudoku）这个名字听起来像纯正的日语（意为“数字必须只出现一次”），但它的现代形式其实最早诞生于 1979 年的美国，当时被称为“Number Place”。直到 1984 年传入日本后才真正风靡全球。</p>
      <p>数独的魅力在于它<strong>不需要任何数学运算能力</strong>，你不需要加减乘除，只需要逻辑推理。</p>
      
      <h3>九宫格、行、列的基本约束</h3>
      <p>标准数独是一个 9x9 的网格，被分为 9 个小的 3x3 区域（称为“宫”或“Box”）。游戏的唯一目标是用数字 1 到 9 填满所有空格，但必须遵守以下三条铁律：</p>
      <ul>
        <li><strong>每一行</strong>（从左到右）必须包含数字 1-9，不能重复。</li>
        <li><strong>每一列</strong>（从上到下）必须包含数字 1-9，不能重复。</li>
        <li><strong>每一宫</strong>（粗线围起来的 3x3 区域）必须包含数字 1-9，不能重复。</li>
      </ul>

      <h3>唯一的解：好数独的标志</h3>
      <p>一个设计优良的数独题目，<strong>一定且均只有一个唯一的终局</strong>。如果你在解题过程中发现需要靠“猜”来推进，那说明你还没有找到当下的逻辑突破口，或者这是一道质量不佳的题目。</p>

      <hr />

      <h2>入门第一步：排除法 (Cross-hatching)</h2>
      <p>这是最直观、最适合新手的技巧，也被称为“扫描法”或“摒除法”。它的核心逻辑是：<strong>寻找某个数字在某个宫内唯一能容身的位置。</strong></p>

      <h3>如何扫描行、列和宫，找到唯一可以填的数字</h3>
      <p>假设我们想填入数字 <strong>1</strong>。</p>
      <ol>
        <li>观察整个盘面，找到已经填好的所有数字 1。</li>
        <li>想象这些数字 1 向四周发射“激光”，它们所在的行和列都不能再出现 1。</li>
        <li>把目光聚焦到某个还没有填入 1 的 3x3 宫内。</li>
        <li>如果这个宫里，除了一个格子外，其他格子都被外面的“激光”扫射到了（即都在被占用的行或列上），那么剩下的那个格子，就是 1 的<strong>唯一容身之所</strong>。</li>
      </ol>

      <hr />

      <h2>入门第二步：唯一余数法 (Sole Candidate)</h2>
      <p>如果说排除法是“为一个数字找位置”，那么唯一余数法就是“为一个位置找数字”。</p>

      <h3>当一个单元格只剩下一个可能性时</h3>
      <p>当你发现某个空格，它所在的行、列、宫里已经出现了 1, 2, 3, 4, 5, 7, 8, 9 这八个数字。那么显而易见，这个格子<strong>只能填 6</strong>。</p>
      <p>这就是“唯一余数法”。虽然听起来很简单，但在复杂的局面中，这需要你极具耐心去清点每个格子的“候选数”。</p>

      <h3>如何与排除法结合使用</h3>
      <ul>
        <li><strong>先扫视（排除法）</strong>：快速填出那些显而易见的数字，打开局面。</li>
        <li><strong>后定点（唯一余数法）</strong>：当局面填得差不多，大片空白变得零散时，盯着某个格子，数一数它还能填什么。</li>
      </ul>

      <hr />

      <h2>新手常犯的 5 个错误及如何避免</h2>
      <ol>
        <li><strong>盲目猜测</strong>：永远不要猜数。一旦猜错，后面全盘皆输。如果卡住了，休息一下，换个角度看。</li>
        <li><strong>忽视笔记</strong>：在格子里用小字体标出候选数（Pencil Marks）是进阶的关键，不要试图把所有可能性都记在脑子里。</li>
        <li><strong>只盯着一个地方</strong>：如果不顺，立刻换一个数字或换一个区域扫描。数独需要全局视野。</li>
        <li><strong>检查不仔细</strong>：填入数字后，顺手检查一下它是否真的不冲突，能避免低级失误。</li>
        <li><strong>急于求成</strong>：享受逻辑推理的过程，而不是只为了填满格子。</li>
      </ol>

      <hr />

      <h2>准备好了吗？开始你的第一局简单数独挑战！</h2>
      <p>理论知识已经储备完毕，现在是检验真理的时候了。我们为你准备了适合新手的入门级题目，应用刚才学到的“排除法”，看看你能在几分钟内解开它！</p>
    `,

    // English
    title_en: 'Sudoku Ultimate Beginner Guide: Master Rules & Tricks in 15 Minutes',
    summary_en: 'Start your Sudoku journey from scratch! Learn the origins, basic rules, Cross-hatching, and Sole Candidate techniques to solve puzzles easily.',
    readTime_en: '15 min read',
    content_en: `
      <h2>What is Sudoku? Origins and Rules</h2>
      <p>The name Sudoku sounds Japanese (meaning "single number"), but its modern form actually originated in the US in 1979 as "Number Place". It only became a global phenomenon after being introduced to Japan in 1984.</p>
      <p>The beauty of Sudoku is that it <strong>requires no math skills</strong>. You don't need addition or subtraction, just pure logic.</p>
      
      <h3>The Grid, Rows, and Columns</h3>
      <p>Standard Sudoku is a 9x9 grid, divided into nine smaller 3x3 areas (called "boxes"). The goal is to fill the grid with numbers 1 to 9, following three strict rules:</p>
      <ul>
        <li><strong>Every row</strong> must contain numbers 1-9 without repetition.</li>
        <li><strong>Every column</strong> must contain numbers 1-9 without repetition.</li>
        <li><strong>Every box</strong> (3x3 area) must contain numbers 1-9 without repetition.</li>
      </ul>

      <h3>Unique Solution: The Mark of Quality</h3>
      <p>A well-designed Sudoku puzzle has <strong>one and only one unique solution</strong>. If you find yourself needing to "guess," you haven't found the logical path yet, or the puzzle is poorly designed.</p>

      <hr />

      <h2>Step 1: Cross-hatching (Scanning)</h2>
      <p>This is the most intuitive technique for beginners. The core logic is: <strong>Find the only possible spot for a number within a box.</strong></p>

      <h3>How to Scan</h3>
      <p>Assuming we want to place the number <strong>1</strong>:</p>
      <ol>
        <li>Look at the grid and find all existing 1s.</li>
        <li>Imagine "lasers" shooting from these 1s horizontally and vertically. No other 1 can exist in their paths.</li>
        <li>Focus on a 3x3 box that doesn't have a 1 yet.</li>
        <li>If all cells in that box are "hit" by lasers except for one, that remaining cell is the <strong>only place</strong> for the 1.</li>
      </ol>

      <hr />

      <h2>Step 2: Sole Candidate</h2>
      <p>Cross-hatching finds a place for a number; Sole Candidate finds a number for a place.</p>

      <h3>When Only One Number Fits</h3>
      <p>If you check a specific empty cell and see that its row, column, and box already contain 1, 2, 3, 4, 5, 7, 8, 9... then obviously, this cell <strong>must be 6</strong>.</p>
      <p>This sounds simple, but in complex games, it requires patience to count "candidates" for each cell.</p>

      <hr />

      <h2>5 Common Mistakes to Avoid</h2>
      <ol>
        <li><strong>Guessing</strong>: Never guess. One wrong guess ruins the whole board. If stuck, take a break.</li>
        <li><strong>Ignoring Notes</strong>: Using pencil marks for candidates is key for advanced play. Don't rely solely on memory.</li>
        <li><strong>Tunnel Vision</strong>: If stuck, switch to a different number or area. Sudoku requires global vision.</li>
        <li><strong>Carelessness</strong>: Double-check every number you place to ensure no conflicts.</li>
        <li><strong>Rushing</strong>: Enjoy the logic, don't just race to finish.</li>
      </ol>

      <hr />

      <h2>Ready to Play?</h2>
      <p>You have the theory. Now test it with our beginner-friendly puzzles!</p>
    `,

    // French
    title_fr: 'Guide Ultime du Sudoku pour Débutants : Règles et Astuces en 15 Minutes',
    summary_fr: 'Maîtrisez le Sudoku ! Apprenez les origines, les règles de base, et les techniques d\'élimination pour résoudre vos premières grilles.',
    readTime_fr: '15 min de lecture',
    content_fr: `
      <h2>Qu'est-ce que le Sudoku ? Origines et Règles</h2>
      <p>Le nom Sudoku sonne japonais (signifiant "chiffre unique"), mais sa forme moderne est née aux États-Unis en 1979 sous le nom de "Number Place". Il n'est devenu un phénomène mondial qu'après son introduction au Japon en 1984.</p>
      <p>La beauté du Sudoku est qu'il <strong>ne nécessite aucune compétence en mathématiques</strong>. Pas d'addition ni de soustraction, juste de la logique pure.</p>
      
      <h3>La Grille, les Lignes et les Colonnes</h3>
      <p>Le Sudoku standard est une grille de 9x9, divisée en neuf zones de 3x3 (appelées "blocs"). Le but est de remplir la grille avec les chiffres de 1 à 9, en respectant trois règles strictes :</p>
      <ul>
        <li><strong>Chaque ligne</strong> doit contenir les chiffres 1-9 sans répétition.</li>
        <li><strong>Chaque colonne</strong> doit contenir les chiffres 1-9 sans répétition.</li>
        <li><strong>Chaque bloc</strong> (zone 3x3) doit contenir les chiffres 1-9 sans répétition.</li>
      </ul>

      <h3>Solution Unique : La Marque de Qualité</h3>
      <p>Un puzzle Sudoku bien conçu a <strong>une et une seule solution unique</strong>. Si vous devez "deviner", c'est que vous avez manqué une déduction logique, ou que le puzzle est mal conçu.</p>

      <hr />

      <h2>Étape 1 : L'Élimination (Cross-hatching)</h2>
      <p>C'est la technique la plus intuitive pour les débutants. La logique est : <strong>Trouver la seule place possible pour un chiffre dans un bloc.</strong></p>

      <h3>Comment Scanner</h3>
      <p>Supposons que nous voulons placer le chiffre <strong>1</strong> :</p>
      <ol>
        <li>Regardez la grille et trouvez tous les 1 existants.</li>
        <li>Imaginez des "lasers" tirés horizontalement et verticalement depuis ces 1. Aucun autre 1 ne peut se trouver sur leur chemin.</li>
        <li>Concentrez-vous sur un bloc 3x3 qui n'a pas encore de 1.</li>
        <li>Si toutes les cases de ce bloc sont "touchées" par les lasers sauf une, cette case restante est la <strong>seule place</strong> pour le 1.</li>
      </ol>

      <hr />

      <h2>Étape 2 : Candidat Unique</h2>
      <p>L'élimination trouve une place pour un chiffre ; le Candidat Unique trouve un chiffre pour une place.</p>

      <h3>Quand un seul chiffre convient</h3>
      <p>Si vous vérifiez une case vide spécifique et voyez que sa ligne, sa colonne et son bloc contiennent déjà 1, 2, 3, 4, 5, 7, 8, 9... alors évidemment, cette case <strong>doit être un 6</strong>.</p>
      
      <hr />

      <h2>5 Erreurs Courantes à Éviter</h2>
      <ol>
        <li><strong>Deviner</strong> : Ne devinez jamais. Une erreur ruine tout.</li>
        <li><strong>Ignorer les notes</strong> : Notez les candidats potentiels.</li>
        <li><strong>Vision tunnel</strong> : Si bloqué, changez de zone.</li>
        <li><strong>Négligence</strong> : Vérifiez chaque placement.</li>
        <li><strong>Précipitation</strong> : Profitez de la logique.</li>
      </ol>

      <hr />

      <h2>Prêt à jouer ?</h2>
      <p>Vous avez la théorie. Testez-la maintenant !</p>
    `,

    category: 'sudoku', // games.sudoku
    date: '2025-12-14',

    relatedGameLink: '/games/sudoku',
    relatedGameText_zh: '去玩数独 (Sudoku)',
    relatedGameText_en: 'Play Sudoku',
    relatedGameText_fr: 'Jouer au Sudoku'
  },
  {
    id: 'poker-hand-rankings',
    // Chinese
    title_zh: '德州扑克牌型大小一览：从皇家同花顺到高牌',
    summary_zh: '记不住牌型大小？这篇指南为你详细列出所有扑克牌型等级，配图说明，让你在牌桌上不再迷茫。',
    readTime_zh: '10 分钟阅读',
    content_zh: '<p>Coming soon...</p>',

    // English
    title_en: 'Poker Hand Rankings: From Royal Flush to High Card',
    summary_en: 'Can\'t remember hand rankings? This guide lists all poker hand hierarchies with illustrations.',
    readTime_en: '10 min read',
    content_en: '<p>Coming soon...</p>',

    // French
    title_fr: 'Classement des Mains de Poker : De la Quinte Flush Royale à la Carte Haute',
    summary_fr: 'Vous ne retenez pas l\'ordre des mains ? Ce guide liste toute la hiérarchie du poker.',
    readTime_fr: '10 min de lecture',
    content_fr: '<p>Bientôt disponible...</p>',

    category: 'poker',
    date: '2025-12-15',
    relatedGameLink: '/games/poker',
    relatedGameText_zh: '去玩扑克拼词',
    relatedGameText_en: 'Play Word Poker',
    relatedGameText_fr: 'Jouer au Poker de Mots'
  }
]
